import React from "react";
import invariant from "invariant";
import {APIEndpoint, Conflict} from "api";
import { FORM_ERROR } from "final-form";


/* Create instance hook
 * The function has two forms: the legacy form and the options form.
 * In the legacy there are 5 arguments create, creating, instance, clear, onCreate
 * in the options form there are only 1 argument, a map of those keys.
 *
 * @param {Function} create - The function used to trigger the creation of the instance
 * @param {Boolean} creating - true if the creation of the instance is ongoing
 * @param {null|Model} instance - the created instance or null
 * @param {Function} instance - A function that clears the instance
 * @param {Function} onCreate - A callback called after a succesful creation
 * @param {Boolean} finalform - when false, the Promise rejects on error (legacy mode)
 *								else the Promise resolves with an error (FinalForm compat)
 *								forced to false when using the legacy form
 *								and defaults to true when using the options form
 *
 * @retuns {Function} - A function returning a Promise on the creation of the instance
 *                      the promise resolve if the instance was created and reject else
 */
export default function useCreate(create, creating, instance, clear, onCreate) {
	// when rejects is true the promise returned by the submit function will
	// reject else it will return an empty value unless an error occurred.
	let finalform = false;

	if (arguments.length === 1) {
		// implement the options form: useCreate({ create: () => {}, creating: true, ... });
		const { endpoint } = create;
		onCreate = create.onCreate;

		if (endpoint){
			finalform = true;
			const { options } = create;
			const getOptions = typeof options === 'function' ? options : () => options;

			invariant(create.create === undefined, 'endpoint is not compatible with update');
			invariant(create.creating === undefined, 'endpoint is not compatible with updating');
			invariant(create.instance === undefined, 'endpoint is not compatible with instance');
			invariant(endpoint instanceof APIEndpoint, `endpoint is not an instance of APIEndpoint but ${endpoint}`);

			const createFN = endpoint.useCreate();
			// eslint-disable-next-line
			create = React.useCallback((data) => createFN(data, getOptions(data)), [])

			creating = endpoint.useCreating();

			instance = endpoint.useCurrent();
		} else {
			({ create, creating, clear=null, instance=null, finalform=true } = create);
		}

	}

	// invariant checks will catch mixed calling forms
	invariant(onCreate === undefined || typeof onCreate === 'function', `onCreate is not a function but ${onCreate}`);
	invariant(typeof create === 'function', `create is not a function but ${create}`);
	invariant(clear === undefined || clear === null || typeof clear === 'function',
		`clear is not a function or null but ${clear}`);
	invariant(typeof creating === 'boolean', 'creating is not a boolean');

	React.useEffect(() => {
		if (clear) {
			clear();
		}
	// eslint-disable-next-line
	}, []);

	const after = React.useRef(null);

	const onSubmit = React.useCallback((values, ...args) => {
		invariant(after.current === null, 'Submit has been called before the resolution of the previous call');

		return new Promise((resolve, reject) => {
			after.current = { values, args, resolve, reject };
			create(values);
		}).finally(() => {
			after.current = null;
		});
	}, [create]);

	React.useEffect(() => {
		if (!creating && after.current) {
			if (instance && !(instance instanceof Conflict)) {
				if (onCreate) {
					const { values, args } = after.current;
					onCreate(instance, values, ...args);
				}
				after.current.resolve(finalform ? null : instance);
			} else if (finalform) {
				const error = instance instanceof Conflict ? instance : { [FORM_ERROR]: '' };
				after.current.resolve(error);
			} else {
				after.current.reject();
			}
		}
	// eslint-disable-next-line
	}, [creating]);

	return onSubmit;
}
